import { useEffect, useState } from 'react';
import { api } from './lib/api.js';
import { SignIn, Centered } from './pages/SignIn.jsx';
import { TwoFactor } from './pages/TwoFactor.jsx';
import { Boarding } from './pages/Boarding.jsx';
import { Shell } from './pages/Shell.jsx';
import { ChangePassword } from './pages/ChangePassword.jsx';
import { Keyboard } from './components/Keyboard.jsx';

/**
 * Wrapped around whatever is on screen, because a till runs full screen and full screen hides
 * the taskbar, which is where Windows keeps its on-screen keyboard. Every screen needs typing
 * at some point, starting with the sign in one.
 */
const withKeyboard = screen => <>{screen}<Keyboard /></>;

export default function App() {
  const [state, setState] = useState({ status: 'loading' });
  const [business, setBusiness] = useState(null);

  async function load() {
    try {
      const { user, businesses } = await api.me();
      setState({ status: 'in', user, businesses });
    } catch {
      setState({ status: 'out' });
    }
  }

  useEffect(() => { load(); }, []);

  async function signOut() {
    await api.signOut().catch(() => {});
    setBusiness(null);
    setState({ status: 'out' });
  }

  if (state.status === 'loading') {
    return <Centered><span className="t-body-m muted">Loading</span></Centered>;
  }

  if (state.status === 'out') {
    return withKeyboard(<SignIn onSignedIn={load} onMfaRequired={user => setState({ status: 'mfa', user })} />);
  }

  if (state.status === 'mfa') {
    return withKeyboard(<TwoFactor pendingUser={state.user} onPassed={load} />);
  }

  // A handed over account stops here. The API refuses everything else anyway, so showing
  // the boarding screen first would only be a door that opens onto a wall.
  if (state.user.mustChangePassword) {
    return withKeyboard(<ChangePassword user={state.user} forced onDone={load} onSignOut={signOut} />);
  }

  if (business) {
    return withKeyboard(<Shell user={state.user} business={business} onLeave={() => setBusiness(null)} onSignOut={signOut} />);
  }

  return withKeyboard(
    <Boarding
      user={state.user}
      businesses={state.businesses}
      onEnter={setBusiness}
      onSignOut={signOut} />
  );
}
